import React from 'react';
import { Button, Form, notification, Steps } from 'antd';
import { gql, useMutation } from '@apollo/client';
import { massAssignFieldTypes } from './MassAssignFieldTypes';
import { StateArray } from '../../util/StateArrayType';
import MassAssignPreview from './MassAssignPreview';

import { useLocalization } from '../../util/useLocalization';
import { Locale } from '../../../localization/LocalizationKeys';
import Puffin from '../../components/Puffin/Puffin';
import {
  AvailableMassAssignFieldsQueryQuery,
  EntityTypeEnum,
  MassAssignFieldAction,
  SelectionCriteriaInput, SubmitMassAssignSingleSelectMutationMutation, SubmitMassAssignSingleSelectMutationMutationVariables
} from '../../../gql/typings';


export type CreateMassAssignData = {
  actionType: string;
  singleSelectValue: string;
  actionName: string;
};


type MassAssignProcessProps = {
  fieldState: StateArray<NonNullable<AvailableMassAssignFieldsQueryQuery['availableMassAssignFields']>['0']|null>;
  entityType: EntityTypeEnum;
  selectionCriteria: SelectionCriteriaInput;
  visibleState: StateArray<boolean>;
  onBusyChange: (busy: boolean) => void;
};

const MassAssignProcess: React.FC<MassAssignProcessProps> = ({
  entityType,
  fieldState: [selectedField, setSelectedField],
  selectionCriteria,
  visibleState,
  onBusyChange,
}) => {
  const localization = useLocalization();
  const [form] = Form.useForm<CreateMassAssignData>();
  const [current, setCurrent] = React.useState(0);
  const inFlight = React.useRef(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [preview, setPreview] = React.useState<{ key: string; fingerprint?: string | null }>();
  const previewKey = JSON.stringify({ selectionCriteria, values: form.getFieldsValue(true) });
  const onPreviewReady = React.useCallback((fingerprint?: string | null) => {
    setPreview({ key: previewKey, fingerprint });
  }, [previewKey]);

  const [actionSelected, setActionSelected] = React.useState<MassAssignFieldAction>(MassAssignFieldAction.SetValueToSelected);
  const [valueSelected, setValueSelected] = React.useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();
  if (!selectedField?.accessRules?.allowMutation) {
    api.open({
      message: <Puffin
        type="explaining"
        loop
        message={localization.formatMessage(Locale.Text.Field_Permission_Access_Denied)}
      />,
    });
  }
  const [
    create,
  ] = useMutation<SubmitMassAssignSingleSelectMutationMutation, SubmitMassAssignSingleSelectMutationMutationVariables>(
    SUBMIT_SINGLE_SELECT_MASS_ASSIGN
  );
  const next = () => {
    setPreview(undefined);
    setCurrent(current + 1);
  };

  if (selectedField === null) {
    return <></>;
  }

  const prev = () => {
    setPreview(undefined);
    setCurrent(current - 1);
  };

  const FieldToDisplay = massAssignFieldTypes[selectedField.fieldType]!;

  const steps = [
    {
      title: 'Assign Values',
      content: (
        <FieldToDisplay
          form={form}
          heading={selectedField.heading}
          isHeadingKey={selectedField.isHeadingKey}
          entityType={entityType}
          actionName={selectedField.actionName}
          actionSelected={[actionSelected, setActionSelected]}
          valueSelected={[valueSelected, setValueSelected]}
        />
      ),
    },
    {
      title: 'Preview',
      content: <MassAssignPreview
        key={previewKey}
        form={form}
        selectionCriteria={selectionCriteria}
        onReady={onPreviewReady}
      />,
    },
  ];

  const onFinish = async () => {
    if (inFlight.current || !preview || preview.key !== previewKey) return;
    inFlight.current = true;
    setSubmitting(true);
    onBusyChange(true);
    const {
      actionType,
      singleSelectValue,
      actionName,
      entityType,
    } = form.getFieldsValue(true);
    try {
      await create({
        variables: {
          actionName,
          selectionCriteria: { ...selectionCriteria, recipientFingerprint: preview.fingerprint },
          input: {
            action: actionType,
            entityType,
            values: singleSelectValue !== undefined ? singleSelectValue.toString() : ''
          },
        },
        refetchQueries: ['PersonsBuiltQuery', 'DefaultCriteriasQuery', 'HCPMoreData', 'HCOMoreData'],

      });
      api.open({
        message: 'Mass Assign has successfully been updated',
        description: 'Your actions have been added to the selected records.'
      });
      visibleState[1](false);
    } catch (error) {
      api.error({ message: 'Mass assignment failed', description: error instanceof Error ? error.message : String(error) });
      setPreview(undefined);
      setCurrent(0);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      onBusyChange(false);
    }
  };

  return (
    <Form form={form} preserve onFinish={onFinish}>
      {contextHolder}
      <Steps
        className="mass-assign-steps-container"
        current={current}
        size="small"
        items={steps.map(item => (
          {
            title: item.title
          }
        ))}
      />
      <div className="steps-content">{steps[current]?.content}</div>

      <div className="steps-action">
        {current < steps.length - 1 && (
          <Button
            disabled={!valueSelected
            && actionSelected
            !== MassAssignFieldAction.BlankOutField && !selectedField.accessRules?.allowMutation}
            type="primary"
            onClick={() => next()}
          >
            {localization.formatMessage(Locale.Command.Next)}
          </Button>
        )}
        {current === steps.length - 1 && (
          <Button
            htmlType="submit"
            type="primary"
            loading={submitting}
            disabled={submitting || !preview || preview.key !== previewKey}
          >
            {localization.formatMessage(Locale.Command.Submit)}
          </Button>
        )}
        {current > 0 && (
          <Button disabled={submitting} style={{ margin: '0 8px' }} onClick={() => prev()}>
            {localization.formatMessage(Locale.Command.Back)}
          </Button>
        )}
        {current == 0 && (
          <Button onClick={() => setSelectedField(null)} style={{ margin: '0 8px' }}>
            {localization.formatMessage(Locale.Command.Back)}
          </Button>
        )}
      </div>
    </Form>

  );
};

const SUBMIT_SINGLE_SELECT_MASS_ASSIGN = gql`
  mutation SubmitMassAssignSingleSelectMutation($actionName: String!,
    $selectionCriteria: SelectionCriteriaInput!,
    $input: MassAssignFieldInput!
  ) {
    executeMassAssign(actionName: $actionName, selection: $selectionCriteria, input: $input)
  }
`;


export default MassAssignProcess;
